import {
    Add,
    FilterAltOffOutlined,
    FilterAltOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    Grid,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GET } from "../../../Utilities/BaseService";
import {
    filterSelectionMessage,
    hasValue,
    noDataMessage,
} from "../../../Utilities/constants";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";
import { currencyFormatter } from "../../Reading/Bill/Cells/Last12Summary";
import BaseTable from "../../Table/BaseTable";
import SearchBar from "../../Table/Components/SearchBar";

type Filter = {
    search: string;
    district: number | "" | "all";
    village: number | "" | "all";
    hamlet: number | "" | "all";
    powerstation: number | "" | "all";
};

const initialFilter: Filter = {
    search: "",
    district: "",
    village: "",
    hamlet: "",
    powerstation: "",
};

const initialClearAll = {
    district: false,
    village: false,
    hamlet: false,
    powerstation: false,
    search: false,
};

const LineRent = () => {
    const [rowsCount, setRowsCount] = useState(0);
    const [pagination, setPagination] = useState<any>({ page: 0, limit: 100 });
    const [load, setLoad] = useState(false);
    const [clearAll, setClearAll] = useState(initialClearAll);
    const [filter, setFilter] = useState<any>(initialFilter);
    const [feedback, setFeedback] = useState("");
    const [changeCount, setChangeCount] = useState(0);
    const [intermediateFilter, setIntermediateFilter] =
        useState<Filter>(initialFilter);
    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));

    const { data, isLoading, isError, isSuccess } = useQuery(
        ["line-rents", pagination.page, pagination.limit, filter],
        () =>
            GET("/line-rent", {
                page: pagination.page + 1,
                limit: pagination.limit,
                ...Object.fromEntries(
                    Object.entries(filter).filter(([k, v]) => v && v !== "all")
                ),
            }),
        {
            enabled: Boolean(changeCount),
            onSuccess(res) {
                if (res) setRowsCount(res?.data.count);
            },
        }
    );

    /* ================= CLEAR ALL ================= */

    const handleClearAll = () => {
        // reset UI filters
        setIntermediateFilter(initialFilter);

        // reset applied filters
        setFilter(initialFilter);

        // notify child inputs
        setClearAll({
            district: true,
            village: true,
            hamlet: true,
            powerstation: true,
            search: true,
        });

        // reset search trigger & pagination
        setChangeCount(0);
        setPagination({ page: 0, limit: 100 });

        // turn flags off after render
        setTimeout(
            () =>
                setClearAll({
                    district: false,
                    village: false,
                    hamlet: false,
                    powerstation: false,
                    search: false,
                }),
            0
        );
    };

    useEffect(() => {
        if (changeCount) {
            setFilter({ ...filter, ...intermediateFilter });
        }
    }, [changeCount]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: mdDown ? "auto" : `calc(100vh - 96px)`,
                // border: "1px solid red",
            }}
        >
            <Card
                elevation={0}
                sx={{
                    bgcolor: theme.palette.common.white,
                    p: 2,
                    mb: "1rem",
                }}
            >
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1.5}
                >
                    <Grid item xs={12} md={4}>
                        <Typography variant="h5" color="GrayText">
                            Line Rents
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6} container spacing={1.5}>
                        <Grid item flex={1}></Grid>

                        <Grid item xs={12} md="auto">
                            <MultiPermissionAuthorize
                                ops={["CREATE LINE_RENT"]}
                            >
                                <Link
                                    to="/connections/line-rent/add"
                                    style={{ textDecoration: "none" }}
                                >
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<Add />}
                                        sx={{ height: "100%" }}
                                        fullWidth
                                    >
                                        add line rent
                                    </Button>
                                </Link>
                            </MultiPermissionAuthorize>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} container spacing={1.5}>
                        <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                            <Grid item xs={12} md={6} lg={3}>
                                <UpdatedSearchableInput
                                    api="/region/district"
                                    label="District"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    clearAll={clearAll.district}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                            <Grid item xs={12} md={6} lg={3}>
                                <UpdatedSearchableInput
                                    api="/powerstation"
                                    label="Powerstation"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={intermediateFilter.district}
                                    params={{
                                        district: intermediateFilter.district,
                                    }}
                                    clearAll={clearAll.powerstation}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                            <Grid item xs={12} md={6} lg={3}>
                                <UpdatedSearchableInput
                                    api="/region/village"
                                    label="Village"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={[
                                        intermediateFilter.district,
                                        intermediateFilter.powerstation,
                                    ]}
                                    params={{
                                        district: intermediateFilter.district,
                                        powerstation:
                                            intermediateFilter.powerstation,
                                    }}
                                    clearAll={clearAll.village}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize ops={["READ HAMLET"]}>
                            <Grid item xs={12} md={6} lg={3}>
                                <UpdatedSearchableInput
                                    api="/region/hamlet"
                                    label="Hamlet"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={[
                                        intermediateFilter.village,
                                        intermediateFilter.powerstation,
                                    ]}
                                    params={{
                                        village: intermediateFilter.village,
                                        powerstation:
                                            intermediateFilter.powerstation,
                                    }}
                                    clearAll={clearAll.hamlet}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <Grid item xs={12} sm={6} lg={3}>
                            <SearchBar
                                filter={intermediateFilter}
                                setFilter={setIntermediateFilter}
                                clear={clearAll.search}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} lg={3}>
                            <Button
                                color="secondary"
                                variant="outlined"
                                onClick={() => setChangeCount(changeCount + 1)}
                                sx={{ height: "100%" }}
                                startIcon={<FilterAltOutlined />}
                                fullWidth
                            >
                                Search
                            </Button>
                        </Grid>

                        {/* CLEAR ALL */}
                        <Grid item xs={12} sm={6} lg={3}>
                            <Button
                                color="error"
                                variant="outlined"
                                startIcon={<FilterAltOffOutlined />}
                                onClick={handleClearAll}
                                sx={{
                                    height: "100%",
                                    minWidth: theme.spacing(20),
                                }}
                                fullWidth
                            >
                                Clear All
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Card>

            {(!changeCount || !data?.data.rows.length || isError) && (
                <Alert severity="info">
                    {!data?.data.rows.length && isSuccess
                        ? noDataMessage
                        : !hasValue(filter) && !changeCount
                        ? filterSelectionMessage
                        : ""}
                </Alert>
            )}

            <Box sx={{ height: "60%" }}>
                <BaseTable
                    headers={{
                        id: "ID",
                        name: "Name",
                        amount: "Charges",
                        district: "District",
                        village: "Village",
                        hamlet: "Hamlet",
                        powerstation: "Powerstation",
                    }}
                    defaultSelectedHeaders={["id", "name", "amount", "regions"]}
                    data={data?.data.rows.map((row: any) => ({
                        ...row,
                        amount: currencyFormatter.format(row.amount),
                        district: row?.district?.name ?? "Global",
                        village: row?.village?.name ?? "Global",
                        hamlet: row?.hamlet?.name ?? "Global",
                        powerstation: row?.powerstation?.name ?? "Global",
                    }))}
                    load={Boolean(changeCount)}
                    delEndPoint="/line-rent"
                    feedback={feedback}
                    setFeedback={setFeedback}
                    rowsCount={rowsCount}
                    pagination={pagination}
                    setPagination={setPagination}
                    isLoading={isLoading}
                    permissions={{
                        edit: ["UPDATE LINE_RENT"],
                        delete: ["DELETE LINE_RENT"],
                    }}
                    queryKey={[
                        "line-rents",
                        pagination.page,
                        pagination.limit,
                        filter,
                    ]}
                    reportName="LineRent__List"
                    reportDetails={{
                        district: filter.district ? filter.district : undefined,
                        powerstation: filter.powerstation
                            ? filter.powerstation
                            : undefined,
                        village: filter.village ? filter.village : undefined,
                        hamlet: filter.hamlet ? filter.hamlet : undefined,
                    }}
                />
            </Box>
        </Box>
    );
};

export default LineRent;
